import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import MobileHeader from './components/MobileHeader';
import MobileSearchBar from './components/MobileSearchBar';
import MobileExpansionSelector from './components/MobileExpansionSelector';
import MobileTileGrid from './components/MobileTileGrid';
import MobileAdditionalBuylistTileGrid from './components/MobileAdditionalBuylistTileGrid';
import MobileCardList from './components/MobileCardList';
import MobileCart from './components/MobileCart';
import MobilePagination from './components/MobilePagination';
import MobileCheckoutPage from './components/MobileCheckoutPage';
import MobileGameSelector from './components/MobileGameSelector';
import MobileCardRecognition from './components/MobileCardRecognition';
import GradedCardsPopup from '../components/GradedCardsPopup';
import VideoGamesList from '../components/VideoGamesList';
import { BACKEND_URL } from '../config';
import './MobileApp.css';

// Currency symbols for formatting prices
const currencySymbols = {
  USD: '$', EUR: '€', GBP: '£', JPY: '¥', CAD: 'C$', AUD: 'A$', CHF: 'CHF', CNY: '¥', SEK: 'kr', NZD: 'NZ$',
  MXN: 'Mex$', SGD: 'S$', HKD: 'HK$', NOK: 'kr', KRW: '₩', TRY: '₺', RUB: '₽', INR: '₹', BRL: 'R$', ZAR: 'R', AED: 'د.إ'
};

const MobileApp = ({
  enabledGames,
  cartItems,
  setCartItems,
  removeFromCart,
  checkout,
  togglePaymentType,
  clearCart,
  userSettings,
  cards,
  addToCart,
  allowedConditions,
  floorPrice,
  bulkSettings,
  excludedSku,
  calculatePrice,
  exchangeRates,
  handleGameChange,
  handleSetChange,
  searchParams,
  setSearchParams,
  isLoading,
  isInitialLoad,
  setIsInitialLoad,
  customItems,
  fetchCustomItems,
  hotlistItems,
  hotlistFixedPrices = {},
  fetchHotlistItems,
  additionalBuylistCategories,
  additionalBuylistItems,
  fetchAdditionalBuylistItems,
  selectedAdditionalCategory,
  username,
  showGradedCardsButton,
  setShowGradedCardsPopup,
  paymentSplitData,
  setPaymentSplitData,
  onLoadBuylist
}) => {
  const { t } = useTranslation();
  const [activeView, setActiveView] = useState('home'); // 'home', 'search', 'cart', 'checkout', 'customItems', 'hotlist', 'cardRecognition', 'additionalBuylist', 'videoGames'
  const [searchTerm, setSearchTerm] = useState('');
  const [showCart, setShowCart] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedGame, setSelectedGame] = useState(searchParams.game || '');
  const [userData, setUserData] = useState(null);
  const [showGradedCardsPopupLocal, setShowGradedCardsPopupLocal] = useState(false);
  const [videoGameConsoleFilter, setVideoGameConsoleFilter] = useState('');
  
  // Function to fetch user data from the collection
  const fetchUserData = useCallback(async () => {
    if (!userSettings || !userSettings.username) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/user/${userSettings.username}`);
      if (response.ok) {
        const data = await response.json();
        console.log('User data fetched:', data);
        setUserData(data);
      } else {
        console.error('Failed to fetch user data');
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  }, [userSettings, BACKEND_URL]);
  
  // Fetch user data when userSettings changes
  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  // Toggle cart visibility when items are added
  useEffect(() => {
    if (cartItems.length > 0 && !showCart) {
      // Don't automatically show cart on mobile, just update the badge
    }
  }, [cartItems.length, showCart]);

  // Update selected game when searchParams.game changes
  useEffect(() => {
    if (searchParams.game) {
      setSelectedGame(searchParams.game);
    }
  }, [searchParams.game]);

  const handleSearchSubmit = (term) => {
    setSearchTerm(term);
    setSearchParams(prev => ({
      ...prev,
      term: term,
      page: 1
    }));
    setIsInitialLoad(false);
    setActiveView('search');
  };

  const handlePageChange = (newPage) => {
    setSearchParams(prev => ({
      ...prev,
      page: parseInt(newPage)
    }));
    // Scroll to top when changing pages
    window.scrollTo(0, 0);
  };

  const handleGameSelect = (game) => {
    // Check if Video Games was selected
    const isVideoGames = game.toLowerCase() === 'video games' || game.toLowerCase() === 'videogames';

    if (isVideoGames) {
      setActiveView('videoGames');
      setSelectedGame(game);
      return;
    }

    setSelectedGame(game);
    handleGameChange({ target: { value: game } });
  };

  const toggleCartView = () => {
    setShowCart(!showCart);
    if (!showCart) {
      setActiveView('cart');
    } else {
      setActiveView('search');
    }
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };


  return (
    <div className="mobile-app">
      <MobileHeader 
        cartItemCount={cartItems.reduce((total, item) => total + item.quantity, 0)} 
        onCartClick={toggleCartView}
        onHomeClick={() => setActiveView('home')}
        onFiltersClick={toggleFilters}
        activeView={activeView}
      />

      {activeView === 'home' && (
        <div className="mobile-home-view">
          <MobileSearchBar onSearch={handleSearchSubmit} />
          
          <div className="mobile-game-tiles">
            <h2>{t('Select a Game')}</h2>
            <MobileTileGrid
              games={enabledGames}
              onGameSelect={(game) => {
                handleGameChange({ target: { value: game } });
                setSelectedGame(game);
                setActiveView('search');
              }}
            />

            <MobileAdditionalBuylistTileGrid
              categories={additionalBuylistCategories}
              onCategorySelect={(category) => {
                console.log('Selected category:', category);
                fetchAdditionalBuylistItems(username, category.product_type);
                setActiveView('additionalBuylist');
              }}
            />

            {showGradedCardsButton && (
              <div className="mobile-custom-items-tile-container">
                <h2>{t('Graded Cards')}</h2>
                <div
                  className="mobile-custom-items-tile"
                  onClick={() => setShowGradedCardsPopupLocal(true)}
                  style={{
                    background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                    cursor: 'pointer'
                  }}
                >
                  <div className="mobile-custom-items-tile-icon">🏆</div>
                  <div className="mobile-custom-items-tile-title">{t('Graded Cards')}</div>
                  <div className="mobile-custom-items-tile-subtitle">{t('Submit your graded cards for evaluation')}</div>
                </div>
              </div>
            )}

            {customItems && customItems.length > 0 && (
              <div className="mobile-custom-items-tile-container">
                <div 
                  className="mobile-custom-items-tile"
                  onClick={() => setActiveView('customItems')}
                >
                  <div className="mobile-custom-items-tile-content">
                    <div className="mobile-custom-items-tile-name">{userSettings?.custom_buylist_button_label || t('We Also Buy')}</div>
                  </div>
                </div>
              </div>
            )}
            
            <div className="mobile-custom-items-tile-container">
              <div 
                className="mobile-custom-items-tile hotlist-tile"
                onClick={() => {
                  setIsInitialLoad(true);
                  fetchHotlistItems(userSettings?.username);
                  setActiveView('hotlist');
                }}
              >
                <div className="mobile-custom-items-tile-content">
                  <div className="mobile-custom-items-tile-name">{t('Hotlist')}</div>
                </div>
              </div>
            </div>
            
            {userSettings?.mobileUse === true && (
              <div className="mobile-custom-items-tile-container">
                <div 
                  className="mobile-custom-items-tile camera-tile"
                  onClick={() => setActiveView('cardRecognition')}
                >
                  <div className="mobile-custom-items-tile-content">
                    <div className="mobile-custom-items-tile-name">{t('Use Camera')}</div>
                  </div>
                </div>
              </div>
            )}
            
            {userData && userData.website && (
              <div className="mobile-store-website-tile-container">
                <div 
                  className="mobile-store-website-tile"
                  onClick={() => window.open(userData.website, '_blank')}
                >
                  <div className="mobile-store-website-tile-content">
                    <div className="mobile-store-website-tile-name">{t('Back to Store')}</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {activeView === 'search' && (
        <div className="mobile-search-view">
          <MobileSearchBar 
            onSearch={handleSearchSubmit} 
            initialValue={searchTerm}
          />
          
          {selectedGame && (
            <MobileGameSelector 
              games={enabledGames}
              selectedGame={selectedGame}
              onGameChange={handleGameSelect}
            />
          )}
          
          
          {showFilters && (
            <div className="mobile-filters">
              <MobileExpansionSelector 
                allExpansionNames={searchParams.allExpansionNames || []}
                searchParams={searchParams}
                handleSetChange={handleSetChange}
              />
            </div>
          )}
          
          {isLoading ? (
            <div className="mobile-loading">
              <p>{t('Loading...')}</p>
            </div>
          ) : isInitialLoad ? (
            <div className="mobile-initial-load">
              <p>{t('Search for cards to get started')}</p>
            </div>
          ) : (
            <>
              <MobileCardList
                cards={cards}
                addToCart={addToCart}
                allowedConditions={allowedConditions}
                calculatePrice={calculatePrice}
                userSettings={userSettings}
                excludedSku={excludedSku}
                cartItems={cartItems}
              />

              {cards.length > 0 && searchParams.totalPages > 1 && (
                <MobilePagination 
                  currentPage={searchParams.page}
                  totalPages={searchParams.totalPages}
                  onPageChange={handlePageChange}
                />
              )}
            </>
          )}
        </div>
      )}

      {activeView === 'cart' && (
        <MobileCart
          cartItems={cartItems}
          setCartItems={setCartItems}
          removeFromCart={removeFromCart}
          togglePaymentType={togglePaymentType}
          checkout={() => setActiveView('checkout')}
          clearCart={clearCart}
          userSettings={userSettings}
          onClose={() => {
            setActiveView('search');
            setShowCart(false);
          }}
          paymentSplitData={paymentSplitData}
          setPaymentSplitData={setPaymentSplitData}
          onLoadBuylist={onLoadBuylist}
        />
      )}

      {activeView === 'customItems' && (
        <div className="mobile-custom-items-view">
          <div className="mobile-view-header">
            <button 
              className="mobile-back-button"
              onClick={() => setActiveView('home')}
            >
              ← {t('Back')}
            </button>
            <h2>{userSettings?.custom_buylist_button_label || t('We Also Buy')}</h2>
          </div>
          
          <div className="mobile-custom-items-container">
            {customItems.map((item) => (
              <div key={item._id.$oid} className="mobile-custom-item">
                <div className="mobile-custom-item-title">{item.title}</div>
                <div className="mobile-custom-item-description">{item.description}</div>
                
                <div className="mobile-custom-item-price">
                  <div className="mobile-custom-item-price-label">{t('Cash Price')}:</div>
                  <div className="mobile-custom-item-price-value">
                    {currencySymbols[userSettings?.currency] || '$'}{item.cash_price}
                  </div>
                </div>
                
                <div className="mobile-custom-item-price">
                  <div className="mobile-custom-item-price-label">{t('Credit Price')}:</div>
                  <div className="mobile-custom-item-price-value">
                    {currencySymbols[userSettings?.currency] || '$'}{item.credit_price}
                  </div>
                </div>
                
                <div className="mobile-custom-item-quantity">
                  <div className="mobile-custom-item-quantity-label">{t('Quantity')}:</div>
                  <input 
                    type="number" 
                    min="1" 
                    max="999" 
                    defaultValue="1"
                    className="mobile-custom-item-quantity-input"
                    id={`quantity-${item._id.$oid}`}
                  />
                </div>
                
                <div className="mobile-custom-item-actions">
                  <button 
                    className="mobile-custom-item-add-cash"
                    onClick={() => {
                      const quantity = parseInt(document.getElementById(`quantity-${item._id.$oid}`).value);
                      const customItem = {
                        name: item.title,
                        description: item.description,
                        cashPrice: item.cash_price,
                        creditPrice: item.credit_price,
                        quantity: quantity,
                        isCash: true,
                        cartItemId: `custom-${item._id.$oid}-${Date.now()}`,
                        condition: 'N/A',
                        printing: 'N/A',
                        isCustomItem: true
                      };
                      addToCart(customItem);
                    }}
                  >
                    {t('Add for Cash')}
                  </button>
                  <button 
                    className="mobile-custom-item-add-credit"
                    onClick={() => {
                      const quantity = parseInt(document.getElementById(`quantity-${item._id.$oid}`).value);
                      const customItem = {
                        name: item.title,
                        description: item.description,
                        cashPrice: item.cash_price,
                        creditPrice: item.credit_price,
                        quantity: quantity,
                        isCash: false,
                        cartItemId: `custom-${item._id.$oid}-${Date.now()}`,
                        condition: 'N/A',
                        printing: 'N/A',
                        isCustomItem: true
                      };
                      addToCart(customItem);
                    }}
                  >
                    {t('Add for Credit')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeView === 'additionalBuylist' && (
        <div className="mobile-custom-items-view">
          <div className="mobile-view-header">
            <button
              className="mobile-back-button"
              onClick={() => setActiveView('home')}
            >
              ← {t('Back')}
            </button>
            <h2>{selectedAdditionalCategory?.button_label || t('Additional Items')}</h2>
          </div>

          {selectedAdditionalCategory?.description && (
            <div className="additional-buylist-description">
              <p>{selectedAdditionalCategory.description}</p>
            </div>
          )}

          <div className="mobile-custom-items-container">
            {additionalBuylistItems.map((product) => (
              product.variants
                .filter(variant => {
                  const cashPrice = parseFloat(variant.cashPrice) || 0;
                  const creditPrice = parseFloat(variant.creditPrice) || 0;
                  return cashPrice > 0 || creditPrice > 0;
                })
                .map((variant, variantIndex) => (
                <div key={`${product.productId}-${variantIndex}`} className="mobile-custom-item">
                  {(variant.image || product.image) && (
                    <div style={{ marginBottom: '10px', textAlign: 'center' }}>
                      <img
                        src={variant.image || product.image}
                        alt={product.title}
                        style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '8px' }}
                      />
                    </div>
                  )}
                  <div className="mobile-custom-item-title">
                    {product.title}{variant.title !== 'Default Title' && ` - ${variant.title}`}
                  </div>

                  <div className="mobile-custom-item-price">
                    <div className="mobile-custom-item-price-label">{t('Cash Price')}:</div>
                    <div className="mobile-custom-item-price-value">
                      {currencySymbols[userSettings?.currency] || '$'}{variant.cashPrice}
                    </div>
                  </div>

                  <div className="mobile-custom-item-price">
                    <div className="mobile-custom-item-price-label">{t('Credit Price')}:</div>
                    <div className="mobile-custom-item-price-value">
                      {currencySymbols[userSettings?.currency] || '$'}{variant.creditPrice}
                    </div>
                  </div>

                  <div className="mobile-custom-item-quantity">
                    <div className="mobile-custom-item-quantity-label">{t('Quantity')}:</div>
                    <input
                      type="number"
                      min="1"
                      max="999"
                      defaultValue="1"
                      className="mobile-custom-item-quantity-input"
                      id={`quantity-${product.productId}-${variantIndex}`}
                    />
                  </div>

                  <div className="mobile-custom-item-actions">
                    <button
                      className="mobile-custom-item-add-cash"
                      onClick={() => {
                        const quantity = parseInt(document.getElementById(`quantity-${product.productId}-${variantIndex}`).value);
                        const basePrice = parseFloat(variant.price);
                        const additionalItem = {
                          name: `${product.title} - ${variant.title}`,
                          cashPrice: parseFloat(variant.cashPrice),
                          creditPrice: parseFloat(variant.creditPrice),
                          quantity: quantity,
                          isCash: true,
                          cartItemId: `additional-${product.productId}-${variantIndex}-${Date.now()}`,
                          condition: 'N/A',
                          printing: 'N/A',
                          isAdditionalBuylist: true,
                          productType: selectedAdditionalCategory?.product_type,
                          basePrice: basePrice,
                          originalBasePrice: basePrice
                        };
                        addToCart(additionalItem);
                      }}
                    >
                      {t('Add for Cash')}
                    </button>
                    <button
                      className="mobile-custom-item-add-credit"
                      onClick={() => {
                        const quantity = parseInt(document.getElementById(`quantity-${product.productId}-${variantIndex}`).value);
                        const basePrice = parseFloat(variant.price);
                        const additionalItem = {
                          name: `${product.title} - ${variant.title}`,
                          cashPrice: parseFloat(variant.cashPrice),
                          creditPrice: parseFloat(variant.creditPrice),
                          quantity: quantity,
                          isCash: false,
                          cartItemId: `additional-${product.productId}-${variantIndex}-${Date.now()}`,
                          condition: 'N/A',
                          printing: 'N/A',
                          isAdditionalBuylist: true,
                          productType: selectedAdditionalCategory?.product_type,
                          basePrice: basePrice,
                          originalBasePrice: basePrice
                        };
                        addToCart(additionalItem);
                      }}
                    >
                      {t('Add for Credit')}
                    </button>
                  </div>
                </div>
              ))
            ))}
          </div>
        </div>
      )}

      {activeView === 'hotlist' && (
        <div className="mobile-hotlist-view">
          <div className="mobile-view-header">
            <button 
              className="mobile-back-button"
              onClick={() => setActiveView('home')}
            >
              ← {t('Back')}
            </button>
            <h2>{t('Hotlist')}</h2>
          </div>
          
          <div className="mobile-hotlist-container">
            {isLoading ? (
              <div className="mobile-loading">
                <p>{t('Loading hotlist items...')}</p>
              </div>
            ) : cards.length > 0 ? (
              <MobileCardList
                cards={cards}
                addToCart={addToCart}
                allowedConditions={allowedConditions}
                calculatePrice={calculatePrice}
                userSettings={userSettings}
                excludedSku={excludedSku}
                cartItems={cartItems}
              />
            ) : (
              <div className="mobile-no-hotlist">
                <p>{t('No hotlist items available.')}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {activeView === 'cardRecognition' && (
        <MobileCardRecognition
          addToCart={addToCart}
          userSettings={userSettings}
          exchangeRates={exchangeRates}
          calculatePrice={calculatePrice}
          allowedConditions={allowedConditions}
          onBack={() => setActiveView('home')}
        />
      )}

      {activeView === 'videoGames' && (
        <div className="mobile-video-games-view">
          <div className="mobile-view-header">
            <button
              className="mobile-back-button"
              onClick={() => setActiveView('home')}
            >
              ← {t('Back')}
            </button>
            <h2>{t('Video Games')}</h2>
          </div>
          <VideoGamesList
            userSettings={userSettings}
            addToCart={addToCart}
            exchangeRates={exchangeRates}
            consoleFilter={videoGameConsoleFilter}
            onConsoleChange={setVideoGameConsoleFilter}
          />
        </div>
      )}

      {activeView === 'checkout' && (
        <MobileCheckoutPage
          cartItems={cartItems}
          removeFromCart={removeFromCart}
          completeCheckout={checkout}
          cancelCheckout={() => setActiveView('cart')}
          goToMainPage={() => setActiveView('home')}
          userSettings={userSettings}
          username={userSettings?.username}
          calculatePrice={calculatePrice}
          togglePaymentType={togglePaymentType}
          resetApp={() => {
            clearCart();
            setActiveView('home');
          }}
          clearCart={clearCart}
          paymentSplitData={paymentSplitData}
        />
      )}

      {showGradedCardsPopupLocal && (
        <GradedCardsPopup
          onClose={() => setShowGradedCardsPopupLocal(false)}
          username={username}
          userSettings={userSettings}
        />
      )}

    </div>
  );
};

export default MobileApp;
